<script setup lang="ts">
import { InvoiceDetails } from '@/Pejer/types/invoice'
import DataStateSkeleton from 'pejer/src/components/DataState/DataStateSkeleton.vue'
import DataStateTableCell from 'pejer/src/components/DataState/DataStateTableCell.vue'
import { Skeleton } from 'pejer/src/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'pejer/src/components/ui/table'
import { DataState } from 'pejer/src/types/types'
import { computed } from 'vue'

const props = defineProps<{
  orderDetails: InvoiceDetails['orderDetails'] | undefined
  dataState: DataState
  hideSummary?: boolean
  paymentStatus?: InvoiceDetails['paymentStatus']
  // Når fakturaen er sendt med, trekkes det som er innbetalt fra «Sum å betale».
  invoice?: InvoiceDetails['invoice']
}>()

const lines = computed(
  () =>
    props.orderDetails?.productLines?.filter((line) => {
      // these are added outside of gross amount, so if this is refunded we want to ignore this.
      // TODO: find better way
      if (
        isCredited.value &&
        (line.productLineType === 'DebtCollectionFee' ||
          line.productLineType === 'ReminderFee' ||
          line.productLineType === 'Interest')
      ) {
        return false
      }
      return true
    }) || []
)
const shippingGross = computed(() => (props.orderDetails?.grossShippingCost ?? 0) / 100)
const shippingTaxPct = computed(() => props.orderDetails?.shippingTaxPercentage ?? 0)

const hideTax = computed(() => {
  return (
    props.orderDetails?.productLines.every((line) => line.taxPercentage === 0) &&
    props.orderDetails?.shippingTaxPercentage === 0
  )
})

function netFromGross(amount: number, taxPct: number) {
  return amount / (1 + taxPct / 100)
}

const isCredited = computed(() => {
  return (
    props.paymentStatus &&
    (props.paymentStatus === 'CancelledPayment' ||
      props.paymentStatus === 'RefundedPayment' ||
      props.paymentStatus === 'RefundInitiated')
  )
})

const totals = computed(() => {
  if (props.dataState.isLoading.value) return { net: 0, tax: 0, gross: 0 }
  if (!props.orderDetails) return { net: 0, tax: 0, gross: 0 }
  let net = 0,
    tax = 0,
    gross = 0,
    interest = 0,
    fees = 0,
    reminderFees = 0

  // Productlines
  for (const line of lines.value) {
    const lineGross = line.grossCost / 100
    const lineNet = netFromGross(lineGross, line.taxPercentage)
    net += lineNet
    tax += lineGross - lineNet
    gross += lineGross
    if (line.productLineType === 'Interest') interest += lineGross
    if (line.productLineType === 'Fee') fees += lineGross
    if (line.productLineType === 'ReminderFee') reminderFees += lineGross
  }

  // Shipping
  const shipNet = netFromGross(shippingGross.value, shippingTaxPct.value)
  net += shipNet
  const shipTax = shippingGross.value - shipNet
  tax += shipTax
  gross += shippingGross.value

  return { net, tax, gross, interest, fees, reminderFees }
})

// invoice.paidAmount er innbetalt minus refundert (KidNorway_Invoice.PaidAmount). Etter en
// delrefusjon står fakturaen som ChargedPayment, og varelinjer + kreditlinje summerer til det
// som ble beholdt. Uten dette viste siden det beholdte beløpet som «Sum å betale» (feedback 1551).
// ChargedPayment betyr oppgjort: kortbetalte fakturaer har paidAmount 0, så da regnes hele summen.
const paid = computed(() => {
  if (!props.invoice || isCredited.value) return 0
  if (props.paymentStatus === 'ChargedPayment') return totals.value.gross
  return Math.min((props.invoice.paidAmount ?? 0) / 100, totals.value.gross)
})

const amountDue = computed(() => {
  if (!props.invoice) return totals.value.gross
  return Math.max(0, totals.value.gross - paid.value)
})
</script>
<template>
  <div>
    <!-- Line table -->
    <Table class="w-full mb-8 rounded-t-lg break-inside-avoid">
      <TableHeader>
        <TableRow class="bg-gray-50 rounded-t-lg">
          <!-- <TableHead class="p-2 text-left"></TableHead> -->
          <TableHead class="p-2 text-left">Vare</TableHead>
          <TableHead class="p-2 text-right">Antall</TableHead>
          <TableHead v-if="!hideTax" class="p-2 text-right">Mva.</TableHead>
          <TableHead class="p-2 text-right">
            Linjetotal <br /><span v-if="!hideTax" class="text-xs italic"
              >(inkl. mva)</span
            ></TableHead
          >
        </TableRow>
      </TableHeader>
      <TableBody>
        <template v-if="!dataState.isLoading.value && !dataState.isError.value">
          <TableRow v-for="(line, index) in lines" :key="index">
            <!-- <TableCell class="max-w-16">
              <img
                v-if="line.imageUrl"
                :src="line.imageUrl"
                :alt="line.name"
                class="w-full object-cover rounded-sm aspect-square"
              />
            </TableCell> -->
            <TableCell class="p-2">
              <p class="font-medium">{{ line.name }}</p>
              <p class="text-sm text-gray-600">{{ line.description }}</p>
            </TableCell>
            <TableCell class="p-2 text-right">{{ line.quantity }}</TableCell>
            <TableCell v-if="!hideTax" class="p-2 text-right"
              >{{ line.taxPercentage.toFixed(2) }}%</TableCell
            >
            <TableCell class="p-2 text-right">{{ (line.grossCost / 100).toFixed(2) }} kr</TableCell>
          </TableRow>
        </template>
        <template v-else>
          <TableRow v-for="index in 2" :key="index">
            <DataStateTableCell :dataState="dataState" class="p-2 w-72">
              <div class="flex flex-col gap-1 w-full">
                <Skeleton class="h-5 w-full" />
                <Skeleton class="h-5 w-full" />
              </div>
            </DataStateTableCell>
            <DataStateTableCell :dataState="dataState" class="p-2">
              <Skeleton class="h-5 w-16 ml-auto" />
            </DataStateTableCell>
            <DataStateTableCell :dataState="dataState" class="p-2">
              <Skeleton class="h-5 w-16 ml-auto" />
            </DataStateTableCell>
            <DataStateTableCell :dataState="dataState" class="p-2">
              <Skeleton class="h-5 w-16 ml-auto" />
            </DataStateTableCell>
          </TableRow>
        </template>
        <!-- Shipping -->
        <TableRow v-if="shippingGross > 0">
          <TableCell class="p-2 font-medium">Frakt</TableCell>
          <TableCell class="p-2 text-right">-</TableCell>
          <DataStateTableCell v-if="!hideTax" :dataState="dataState" class="p-2 text-right"
            >{{ shippingTaxPct.toFixed(2) }}%</DataStateTableCell
          >
          <DataStateTableCell v-if="!hideTax" :dataState="dataState" class="p-2 text-right"
            >{{ shippingGross.toFixed(2) }} kr</DataStateTableCell
          >
        </TableRow>
      </TableBody>
    </Table>

    <!-- Summering -->
    <div class="flex justify-end px-2 break-inside-avoid" v-if="!hideSummary">
      <div class="w-1/3 text-sm">
        <!-- <div
        class="bg-slate-100 p-2 mb-4 border-slate-300 border"
        v-if="totals.interest > 0 || totals.reminderFees > 0"
      >
        <div v-if="totals.interest > 0" class="flex justify-between py-1">
          <span>Påløpte renter:</span>
          <DataStateSkeleton :dataState="dataState"
            >{{ totals.interest.toFixed(2) }} kr</DataStateSkeleton
          >
        </div>
        <div v-if="totals.reminderFees > 0" class="flex justify-between py-1">
          <span>Purregebyr:</span>
          <DataStateSkeleton :dataState="dataState"
            >{{ totals.reminderFees.toFixed(2) }} kr</DataStateSkeleton
          >
        </div>
      </div> -->
        <!-- <Separator v-if="totals.interest > 0 || totals.reminderFees > 0" class="my-2 bg-slate-300" /> -->
        <div v-if="!hideTax" class="flex justify-between py-1">
          <span>Netto totalt:</span>
          <DataStateSkeleton :dataState="dataState"
            >{{ totals.net.toFixed(2) }} kr</DataStateSkeleton
          >
        </div>
        <div v-if="!hideTax" class="flex justify-between py-1">
          <span>MVA totalt:</span>
          <DataStateSkeleton :dataState="dataState"
            >{{ totals.tax.toFixed(2) }} kr</DataStateSkeleton
          >
        </div>
        <template v-if="paid > 0">
          <div class="flex justify-between py-1" :class="{ 'mt-2': !hideTax }">
            <span>Totalt:</span>
            <DataStateSkeleton :dataState="dataState"
              >{{ totals.gross.toFixed(2) }} kr</DataStateSkeleton
            >
          </div>
          <div class="flex justify-between py-1">
            <span>Innbetalt:</span>
            <DataStateSkeleton :dataState="dataState">{{ paid.toFixed(2) }} kr</DataStateSkeleton>
          </div>
        </template>
        <div
          class="border-t border-gray-300 pt-2 flex justify-between font-semibold"
          :class="{
            'mt-2': !hideTax || paid > 0,
          }"
        >
          <span>Sum å betale:</span>
          <DataStateSkeleton :dataState="dataState"
            >{{ amountDue.toFixed(2) }} kr</DataStateSkeleton
          >
        </div>
      </div>
    </div>
  </div>
</template>
